import { Grid, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  ActionsPopup,
  BasicTable,
  Button,
  CardComponent,
  cellStyles4,
  Chip,
  ChipSelect,
  MatrixTable,
  NoDataVisual,
  Switch,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import React from 'react';
import { Columns as MatrixColumns } from '@HomaHealth/ui-component-library/lib/_AIQ-components/organisms/MatrixTable';
import { Typography } from '@HomaHealth/ui-component-library';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Divider from '../../atoms/Divider';

interface ProvisionMatrixProps {
  showRegenerate?: boolean;
  handleRegenerateProvisionMatrix?: () => void;
  title?: string;
  subTitle?: string;
  actionsMenuItems?: { label: string; icon: React.ReactNode }[];
   
  handleMatrixActions?: (action: string) => void;
  filterOptions?: string[];
  selectedFilters?: string[];
  setSelectedFilters?: React.Dispatch<React.SetStateAction<string[]>>;
  handleAddFilters?: () => void;
  matrixColumns?: MatrixColumns[];
  matrixData: any[];
   
  handleMatrixSort?: (fieldName: string) => void;
  matrixSortConfig?: {
    fieldName: string;
    direction: 'desc' | 'asc' | null;
  };
  showAddMore?: boolean;
  handleAddMore?: () => void;
  groupByCategory?: boolean;
  showFilters?: boolean;
  toggle?: React.ReactNode;
  showSwitch?: boolean;
  switchValue?: boolean;
  setSwitchValue?: React.Dispatch<React.SetStateAction<boolean>>;
  handleSwitchChange?: () => void;
   
  handleChunkClick?: (rowData: any) => void;
}

const useStyles = makeStyles()({
  flex: {
    display: 'flex',
    gap: '24px',
    alignItems: 'center',
  },
});

const ProvisionMatrix: React.FC<ProvisionMatrixProps> = ({
  showRegenerate = false,
  handleRegenerateProvisionMatrix,
  title = 'Provision Matrix',
  subTitle,
  handleMatrixActions,
  filterOptions = [],
  selectedFilters = [],
  setSelectedFilters = () => {},
  handleAddFilters = () => {},
  matrixColumns,
  matrixData,
  handleMatrixSort = () => {},
  matrixSortConfig = { fieldName: '', direction: null },
  showAddMore = false,
  handleAddMore = () => {},
  groupByCategory = true,
  actionsMenuItems = [],
  showFilters = true,
  toggle,
  showSwitch = false,
  setSwitchValue,
  switchValue = true,
  handleSwitchChange,
  handleChunkClick,
}) => {
  const { classes } = useStyles();
  const GetStatusChip = (importance: string) => {
    let color: any = '';
    let bgColor: any = '';
    switch (importance) {
      case 'Negotiable':
        color = patientAccessTheme.palette.primary?.main;
        bgColor = '#96A3D91A';
        break;
      case 'Good to Have':
        color = '#9747B0';
        bgColor = '#BD96D94D';
        break;
      case 'Mandatory':
        color = '#008272';
        bgColor = '#9DD9961A';
        break;
      case 'Not So Important':
        color = patientAccessTheme.palette.warning[400];
        bgColor = '#feffe6';
        break;
      default:
        color = '#D6545E';
        bgColor = '#FBEEEE';
        break;
    }
    return (
      <Chip
        label={
          <Typography
            fontSize="12px"
            lineHeight="15px"
            fontType="medium"
            textColor={color}
          >
            {importance}
          </Typography>
        }
        backgroundColor={bgColor}
        border={`0.5px solid ${color}`}
        borderRadius="4px"
        height="25px"
      />
    );
  };
  return (
    <Grid>
      {showRegenerate && (
        <Grid style={{ marginBottom: '10px' }}>
          <Button
            label={'Regenerate'}
            variant="contained"
            height="37px"
            width="auto"
            onClick={handleRegenerateProvisionMatrix}
          />
        </Grid>
      )}

      <CardComponent
        title={title}
        subTitle={subTitle}
        topRightItem={
          <Grid className={classes.flex}>
            {showSwitch && (
              <Switch
                checked={switchValue}
                handleChange={() => {
                  handleSwitchChange?.();
                  setSwitchValue?.((prev) => !prev);
                }}
              />
            )}
            {toggle}
            {showFilters && (
              <ChipSelect
                placeholder={'Filter by Clause'}
                options={filterOptions}
                selectedItems={selectedFilters}
                setSelectedItems={setSelectedFilters}
                handleAddColumns={handleAddFilters}
                addButtonLabel="Apply Changes"
              />
            )}
            {actionsMenuItems?.length > 0 && (
              <ActionsPopup
                menuItems={actionsMenuItems}
                handleMenuItemClick={handleMatrixActions}
              />
            )}
          </Grid>
        }
        width="100%"
        body={
          <Grid style={{ marginTop: '10px' }}>
            {matrixData?.length > 0 ? (
              showSwitch && switchValue ? (
                matrixData.map((pageItem: any) => (
                  <BasicTable
                    hideTableHeader={true}
                    key={pageItem.page}
                    columns={[
                      {
                        name:
                          pageItem?.page === null
                            ? 'Additional Provisions'
                            : `Page ${pageItem.page + 1}`,
                        align: 'left',
                        cellStyles: cellStyles4,
                        width: '100%',
                      },
                    ]}
                    rowsData={pageItem?.chunks?.map((chunk: any) => [
                      pageItem?.page === null
                        ? chunk.chunkName
                        : `${chunk.chunkName}`,
                      <>
                        {
                           
                          chunk?.provisions?.map(
                             
                            (prov: any, provIndex: number) => (
                              <Grid key={provIndex}>
                                <Grid container style={{ gap: '7px' }}>
                                  <Typography
                                    fontSize="14px"
                                    lineHeight="22px"
                                    fontType="semiBold"
                                    textColor={
                                      patientAccessTheme.palette.text
                                        .highemphasis
                                    }
                                    margin="0px 0px 16px 0px"
                                  >
                                    {prov.title}
                                  </Typography>
                                  {prov?.check && (
                                    <Tooltip
                                      disableFocusListener
                                      placement="top"
                                      title={
                                        <Typography fontSize="12px">
                                          {prov.check}
                                        </Typography>
                                      }
                                      arrow
                                    >
                                      <InfoOutlinedIcon
                                        style={{
                                          width: '17px',
                                          height: '17px',
                                          fill:
                                            patientAccessTheme.palette.status
                                              .icon,
                                          cursor: 'pointer',
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                  {prov?.importance &&
                                    GetStatusChip(prov.importance)}
                                </Grid>

                                <Typography
                                  fontSize="14px"
                                  lineHeight="22px"
                                  fontType="regular"
                                  textColor={
                                    patientAccessTheme.palette.text.highemphasis
                                  }
                                  margin="0px 0px 16px 0px"
                                >
                                  <b>Why it matters?</b> {prov.description}
                                </Typography>
                                {prov?.legalDoctrine?.length > 0 && (
                                  <Typography
                                    fontSize="14px"
                                    lineHeight="22px"
                                    fontType="regular"
                                    textColor={
                                      patientAccessTheme.palette.text
                                        .highemphasis
                                    }
                                    margin="0px 0px 16px 0px"
                                  >
                                    <b>Legal Doctrine:</b>{' '}
                                    {prov.legalDoctrine.join('|')}
                                  </Typography>
                                )}
                                {prov?.legalCitation?.length > 0 && (
                                  <Typography
                                    fontSize="14px"
                                    lineHeight="22px"
                                    fontType="regular"
                                    textColor={
                                      patientAccessTheme.palette.text
                                        .highemphasis
                                    }
                                    margin="0px 0px 16px 0px"
                                  >
                                    <b>Legal Citation:</b>{' '}
                                    {prov.legalCitation?.join('|')}
                                  </Typography>
                                )}

                                {provIndex < chunk.provisions.length - 1 && (
                                  <Divider
                                    marginTop="20px"
                                    marginBottom="20px"
                                  />
                                )}
                              </Grid>
                            ),
                          )
                        }
                      </>,
                    ])}
                    isCollapsiblePatientTable
                    isFullTable
                    displayType="row"
                    cellHeight="36px"
                    width="100%"
                    handleOnClick={handleChunkClick}
                  />
                ))
              ) : (
                <MatrixTable
                  columns={matrixColumns ?? []}
                  rowsData={matrixData}
                  handleSort={handleMatrixSort}
                  sortConfig={matrixSortConfig}
                  showAddMore={showAddMore}
                  handleAddMore={handleAddMore}
                  groupByCategory={groupByCategory}
                  tableHeight="100%"
                />
              )
            ) : (
              <NoDataVisual content="No provisions found" />
            )}
          </Grid>
        }
      />
    </Grid>
  );
};

export default ProvisionMatrix;
